import { Client } from "@/Common/Api/Client";
import { EmailContact } from "@/Common/Api/Contacts/Dto/EmailContact";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { EmailContactEdit } from "./Profile/ContactComponents/EmailContactEdit";

interface IPatientModifyEmailPopupProps {
    emailContacts: EmailContact[];
    doNotEmail: boolean;
}

class PatientModifyEmailPopupState {
    public doNotEmail: boolean;
    public emailContacts: EmailContact[];
    public emailsToDelete: string[];

    public constructor(init?: PatientModifyEmailPopupState) {
        Object.assign(this, init);
    }

    public static default(): PatientModifyEmailPopupState {
        return new PatientModifyEmailPopupState({
            doNotEmail: false,
            emailContacts: [],
            emailsToDelete: []
        });
    }
}

class PatientModifyEmailPopup extends BaseReactComponent<IPatientModifyEmailPopupProps, PatientModifyEmailPopupState> {
    private id: string = "patientmodifyemailspopupmenu";

    public constructor(props: IPatientModifyEmailPopupProps) {
        super(props);

        const initialState = new PatientModifyEmailPopupState();
        initialState.emailsToDelete = [];
        initialState.emailContacts = props.emailContacts;
        initialState.doNotEmail = props.doNotEmail;
        if (!props.doNotEmail && !initialState.emailContacts?.length)
            initialState.emailContacts = [EmailContact.newEmail()];

        this.state = initialState;
    }

    public componentDidMount(): void {
        super.setOverlay(this.id + "overlay", { onClicked: () => this.onClose() });
    }

    public render(): JSX.Element {
        // fetch email validation info
        let doNotEmailLabelKey = isArrayNullOrUndefinedOrEmpty(this.props.emailContacts) ? "DoNotEmailLabel" : "DoNotEmailWithEmailRemovedLabel";

        return (
            <Popup Id={this.id} title={cacheStore.GetString("ChangeEmailsLabel")} draggable={true} cssClass="memo" onCloseDelegate={() => this.onClose()}>
                <div className="p-body" onKeyDown={e => this.onKeyDown(e)}>
                    <div key="dotNotEmailCheckboxCard" style={{ marginBottom: "20px" }}>
                        <div>
                            <CheckboxControl
                                id="doNotEmailCheck"
                                key="doNotEmailCheck"
                                checked={this.state.doNotEmail}
                                onChange={isChecked => this.mutateState(s => s.doNotEmail = isChecked)}
                                label={cacheStore.GetString(doNotEmailLabelKey)}
                            />
                        </div>
                    </div>
                    <div style={{ overflowY: "auto", maxHeight: "300px", overflowX: "hidden" }}>
                        {!this.state.doNotEmail &&
                            this.state.emailContacts.map(emailModel => (
                                <EmailContactEdit
                                    key={`emailContact_${emailModel.renderId}`}
                                    labelColumnWidth="100px"
                                    email={emailModel}
                                    onContactTypeChange={value => this.mutateEmail(emailModel, email => email.ContactPointTypeLookupId = value)}
                                    onDefaultNotificationChange={checked => this.mutateEmail(emailModel, email => email.NotificationsEnabled = checked)}
                                    onEmailChange={(_, value) => this.mutateEmail(emailModel, email => email.Address = value)}
                                    onOkToContactChange={checked => this.mutateEmail(emailModel, email => email.IsOkToContact = checked)}
                                    onRemove={() => this.mutateState(
                                        s => {
                                            if (emailModel.Id == GuidHelper.Empty())
                                                s.emailContacts = s.emailContacts.filter(email => email.renderId != emailModel.renderId);
                                            else {
                                                s.emailsToDelete.push(emailModel.Id);
                                                s.emailContacts = s.emailContacts.filter(email => email.Id != emailModel.Id);
                                            }
                                        }
                                    )}
                                    containerStyles={{ marginBottom: "20px" }}
                                />
                            ))
                        }
                    </div>
                    {!this.state.doNotEmail && (
                        <div className="flex-container" style={{ marginTop: "5px", marginBottom: "20px", justifyContent: "flex-end" }}>
                            <span style={{ marginRight: "50px" }}>
                                <a onClick={() => this.onAddEmail()}>
                                    {cacheStore.GetString("AddLabel")}
                                </a>
                            </span>
                        </div>
                    )}
                </div>
                <div className="p-footer">
                    <div className="text-right">
                        <button
                            data-action="cancel"
                            className="btn btn-default push-right-5"
                            onClick={() => this.onClose()}
                        >
                            {cacheStore.GetString("CancelLabel")}
                        </button>
                        <button
                            className="btn btn-primary"
                            disabled={!this.canSave()}
                            onClick={() => this.savePopup()}
                        >
                            {cacheStore.GetString("SaveLabel")}
                        </button>
                    </div>
                </div>
            </Popup>
        );
    }

    private mutateEmail(item: EmailContact, mutation: (value: EmailContact) => void): void {
        this.mutateState(s => {
            s.emailContacts = this.state.emailContacts.map(element => {
                if (element.renderId == item.renderId)
                    mutation(element);
                return element;
            });
        });
    }

    private onKeyDown(e: React.KeyboardEvent): void {
        if (e.keyCode === 13 && this.canSave()) // enter
            this.savePopup();

        if (e.keyCode === 27) // escape
            this.onClose();
    }

    private canSave(): boolean {
        return this.state.doNotEmail || this.state.emailContacts.every(e => !EmailContact.isEmpty(e) && e.isValid());
    }

    private onAddEmail(): void {
        let newEmail = EmailContact.newEmail();
        newEmail.ContactId = patientStore.getCurrentPatientContactId();

        this.mutateState(s => s.emailContacts = s.emailContacts.concat(newEmail));
    }

    private onClose(): void {
        sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_CloseEmailsClick);
    }

    private savePopup(): void {
        let patientContactId = patientStore.getCurrentPatientContactId();

        const requestData = {
            PatientContactEmailsDTO: {
                PatientContactId: patientContactId,
                EmailContacts: this.state.doNotEmail ? [] : this.state.emailContacts,
                DoNotEmail: this.state.doNotEmail,
                EmailContactPointIdsToDelete: this.state.emailsToDelete
            }
        };

        Client.Post(
            ServerOperations.Server.Myle_Patient_Contacts_Emails_Update,
            requestData,
            (response, err) => {
                if (err.length > 0)
                    return sessionStore.showPopup(err);

                patientStore.updateContacts(response);
                patientStore.EmitChangeToListeners();
            }
        );
    }
}

export { PatientModifyEmailPopup };
